/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class PropertiesPaneDOMV2
 * @hideconstructor
 * @classdesc API class that is used to manage Slideout functionality
 * (including the corrsponding view) across API calls
 */
define([
	'react-dom',
	'react',
	'mobx',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./PropertiesPaneDOMV2API',
	'./PropertiesView',
	'../../../dashboard/contentpane/PropertyUIControlsFactory',
	/** controls */
	'../../../dashboard/contentpane/controls/InputLabel',
	'../../../dashboard/contentpane/controls/ToggleButton',
	'../../../dashboard/contentpane/controls/DropDown',
	'../../../dashboard/contentpane/controls/Split',
	'../../../dashboard/contentpane/controls/SingleLineLinks',
	'../../../dashboard/contentpane/controls/Slider',
	'../../../dashboard/contentpane/controls/ToggledCombo'
], function(
	ReactDOM,
	React,
	Mobx,
	APIFactory,
	PropertiesPaneDOMV2API,
	PropertiesView,
	PropertyUIControlsFactory,

	/** controls */
	InputLabel,
	ToggleButton,
	DropDown,
	Split,
	SingleLineLinks,
	Slider,
	ToggledCombo
) {

	// TODO: temporary here; in the future will dynamically load
	// control type (key) => path to component (value);
	const BUILTIN_CONTROLS = {
		InputLabel: InputLabel,
		ToggleButton: ToggleButton,
		DropDown: DropDown,
		Split: Split,
		SingleLineLinks: SingleLineLinks,
		UiSlider: Slider,
		ToggledCombo: ToggledCombo
	};

	class PropertiesPaneDOMV2 {

		constructor({ features }) {
			this._dashboard = features.API;
			this._properties = features.PropertiesPane;
			this._slideout = features.InAppSlideoutDOM;
			this._api = APIFactory.createAPI(this, [PropertiesPaneDOMV2API]);
			this._mobxDisposeCallbacks = [];
			this._propertyControlsFactory = new PropertyUIControlsFactory({
				controls: BUILTIN_CONTROLS,
				dashboard: this._dashboard
			});

			this._initialize();
		}

		getAPI() {
			return this._api;
		}

		isEnabled() {
			const featureChecker = this._dashboard.getGlassCoreSvc('.FeatureChecker');
			return !!(featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled'));
		}

		getProviderId() {
			return this._properties.getProviderId();
		}

		_initialize() {
			if (this.isEnabled()) {
				this._slideout.registerView(this.getProviderId(), this._api);
			}
		}

		render(options) {
			const { parentNode } = options;
			return new Promise((resolve) => {
				this._mobxDisposeCallbacks.push(
					Mobx.autorun(() => {
						const propertyFeature = this._dashboard.getFeature('InternalProperties');
						const propertyLayoutList = options.isChild ? options.items: propertyFeature.getPropertyLayoutList();
						const propertyList = propertyFeature.getPropertyList();
						const selectedContentIds = propertyFeature.getSelectedContentIds();
						ReactDOM.render(
							<PropertiesView
								selectedContentIds={selectedContentIds}
								propertyList={propertyList}
								propertyLayoutList={propertyLayoutList}
								propertyControlsFactory={this._propertyControlsFactory}
								context = { propertyFeature.getContext()}
							/>,
							parentNode,
							resolve
						);
					})
				);
			});
		}

		destroy() {
			this._mobxDisposeCallbacks.forEach(cb => cb());
			this._mobxDisposeCallbacks = [];
			this._api = null;
			this._dashboard = null;
			this._properties = null;
			this._slideout = null;
		}
	}

	return PropertiesPaneDOMV2;
});
